<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import SalesReceiptConflictCard from './SalesReceiptConflictCard.vue';

const props = defineProps({
  analysis: { type: Object, required: true },
  canResolve: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  resolvingId: { type: [Number, String], default: null },
});

const emit = defineEmits(['confirm', 'correct', 'edit']);
const { t } = useI18n();
const groups = computed(() => {
  const values = { compradores: [], documento: [], imovel: [], vendedores: [] };
  props.analysis.conflicts?.forEach(conflict =>
    values[conflict.type]?.push(conflict)
  );
  return values;
});
const groupLabel = type => {
  if (type === 'compradores')
    return t('SALES.DELIVERY.ANALYSIS.GROUPS.COMPRADORES');
  if (type === 'documento')
    return t('SALES.DELIVERY.ANALYSIS.GROUPS.DOCUMENTO');
  if (type === 'vendedores')
    return t('SALES.DELIVERY.ANALYSIS.GROUPS.VENDEDORES');
  return t('SALES.DELIVERY.ANALYSIS.GROUPS.IMOVEL');
};
const statusLabel = status =>
  status === 'waiting_ocr'
    ? t('SALES.DELIVERY.ANALYSIS.WAITING_OCR')
    : t('SALES.DELIVERY.ANALYSIS.NOT_STARTED');
</script>

<template>
  <LaisCard
    :title="t('SALES.DELIVERY.ANALYSIS.TITLE')"
    prefix-icon="i-lucide-scan-text"
  >
    <div
      v-if="loading"
      class="h-24 animate-pulse rounded-lais-sm bg-lais-bg-soft"
    />
    <template v-else>
      <p class="text-sm text-lais-text-body">
        {{ t('SALES.DELIVERY.ANALYSIS.DESCRIPTION') }}
      </p>
      <div class="mt-4 flex flex-wrap gap-3 text-sm">
        <span class="font-semibold text-lais-primary">
          {{ t('SALES.DELIVERY.ANALYSIS.PROGRESS', analysis.progress) }}
        </span>
        <span v-if="analysis.filename" class="text-lais-text-muted">
          {{ analysis.filename }}
        </span>
      </div>
      <p
        v-if="['not_started', 'waiting_ocr'].includes(analysis.status)"
        class="mt-4 rounded-lais-sm bg-amber-50 p-3 text-sm text-amber-800"
      >
        {{ statusLabel(analysis.status) }}
      </p>
      <p
        v-else-if="!analysis.conflicts?.length"
        class="mt-4 rounded-lais-sm bg-emerald-50 p-3 text-sm text-emerald-800"
      >
        {{ t('SALES.DELIVERY.ANALYSIS.NO_CONFLICTS') }}
      </p>
      <section
        v-for="(conflicts, type) in groups"
        v-show="conflicts.length"
        :key="type"
        class="mt-5"
      >
        <h3
          class="text-sm font-bold uppercase tracking-wide text-lais-text-muted"
        >
          {{ groupLabel(type) }}
        </h3>
        <div class="mt-3 grid gap-3">
          <SalesReceiptConflictCard
            v-for="conflict in conflicts"
            :key="conflict.id"
            :conflict="conflict"
            :disabled="!canResolve"
            :loading="String(resolvingId) === String(conflict.id)"
            @confirm="emit('confirm', conflict)"
            @correct="emit('correct', conflict)"
            @edit="emit('edit', conflict)"
          />
        </div>
      </section>
    </template>
  </LaisCard>
</template>
